import { useCallback, useEffect, useState } from 'react';
import { getCategories } from '@/services/listingService';
import { produceArt, type ProduceIconSource } from '@/components/produceIcon/produceArt';
import type { Category, Listing } from '@/types/domain';

/** Used before the list loads, or when a listing points at a category that no longer exists. */
const FALLBACK_ICON = '🌾';

let inFlight: Promise<Category[]> | null = null;

/**
 * The category list is small, static and needed by several unrelated surfaces
 * at once (favorites, İlanlarım, search suggestions, producer profile). Cache
 * the promise so mounting four of them costs one request, and drop the cache on
 * failure so a transient error does not poison the page.
 */
function loadCategories(): Promise<Category[]> {
  if (!inFlight) {
    inFlight = getCategories().catch((err) => {
      inFlight = null;
      throw err;
    });
  }
  return inFlight;
}

/**
 * The icon a photo-less listing shows — the design team's art where it exists,
 * an emoji where it does not yet.
 *
 * The product's own icon wins and arrives on the listing itself, so it is right
 * on first paint. Only a listing with no product falls back to its category,
 * which is the one case where nothing more specific exists. For the same reason
 * the product's EMOJI still beats the category's ART: a specific emoji says
 * more than a general picture.
 */
export function useListingIcons(): (
  listing: Pick<Listing, 'categoryId' | 'productIcon' | 'productIconKey'>
) => ProduceIconSource {
  const [categories, setCategories] = useState<Map<string, Category>>(new Map());

  useEffect(() => {
    let alive = true;
    loadCategories()
      .then((cats) => {
        if (alive) setCategories(new Map(cats.map((c) => [c.id, c])));
      })
      // A missing icon is cosmetic — never surface this as an error state.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // Stable between renders: the map keeps this in a useEffect dependency array,
  // and a fresh closure each render would rebuild every pin on every render.
  return useCallback(
    (listing) => {
      const productArt = produceArt(listing.productIconKey);
      if (productArt || listing.productIcon) {
        return { art: productArt, emoji: listing.productIcon || FALLBACK_ICON };
      }
      const category = categories.get(listing.categoryId);
      return category
        ? { art: produceArt(category.iconKey), emoji: category.icon || FALLBACK_ICON }
        : { art: null, emoji: FALLBACK_ICON };
    },
    [categories]
  );
}
